import { Chip } from '@xefi/x-react/chip';
import { Dropdown, type DropdownSectionConfig } from '@xefi/x-react/dropdown';

import { IconChevronDown } from '@xefi/x-react/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Status } from '@/models/status';
import { getStatusStyle } from '@/utils/common';
import { AVAILABLE_STATUSES_BY_LABEL, SURVEY_STATUS } from '@/utils/constants';

type ChipStatusDropdownProps = {
   isDisabled?: boolean;
   statuses: Status[];
   currentStatus: Status;
   onStatusChange?: (status: Status) => void;
};

export const ChipStatusDropdown = ({
   onStatusChange,
   isDisabled = false,
   statuses,
   currentStatus,
}: ChipStatusDropdownProps) => {
   const { t } = useTranslation();
   const [isOpen, setIsOpen] = useState(false);

   const availableStatuses = currentStatus?.label
      ? statuses.filter((status) =>
           AVAILABLE_STATUSES_BY_LABEL[
              currentStatus.label as keyof typeof AVAILABLE_STATUSES_BY_LABEL
           ]?.includes(status.label),
        )
      : [];

   const handleStatusSelect = (status: Status) => {
      setIsOpen(false);
      onStatusChange?.(status);
   };

   const sections: DropdownSectionConfig[] = [
      {
         key: 'status-options',
         items: availableStatuses.map((status) => ({
            key: status.id.toString(),
            label: t(`survey.status.${status.label}`),
            className:
               'cursor-pointer rounded-full border h-7 mb-2 text-xs min-w-24 w-fit flex items-center text-xs justify-center text-center hover:opacity-80 transition-all',
            style: getStatusStyle(status.label),
            onClick: () => handleStatusSelect(status),
         })),
      },
   ];

   if (isDisabled || availableStatuses.length === 0) {
      return (
         <Chip
            style={getStatusStyle(currentStatus?.label ?? SURVEY_STATUS.DRAFT)}
         >
            {t(`survey.status.${currentStatus?.label ?? SURVEY_STATUS.DRAFT}`)}
         </Chip>
      );
   }

   return (
      <Dropdown
         isOpen={isOpen}
         onOpenChange={setIsOpen}
         placement='bottom-start'
         sections={sections}
         classNames={{
            base: 'mt-2',
            content: 'min-w-24',
         }}
         trigger={
            <Chip
               className='flex min-w-24 cursor-pointer justify-center gap-1 border text-xs'
               style={getStatusStyle(
                  currentStatus?.label ?? SURVEY_STATUS.DRAFT,
               )}
               onClick={(event) => event.stopPropagation()}
            >
               <span className='flex items-center gap-1'>
                  {t(
                     `survey.status.${currentStatus?.label ?? SURVEY_STATUS.DRAFT}`,
                  )}
                  <IconChevronDown size={18} className='mt-0.5 opacity-70' />
               </span>
            </Chip>
         }
      />
   );
};
